import React from 'react';
import PageHero from '../components/common/PageHero';
import { Section, SectionHeading } from '../components/common/Section';
import BookCall from '../components/common/BookCall';

const PILLARS = [
  {
    n: '01',
    title: 'Lived, not theorised',
    body: 'Seventeen years inside a family business. The dynamics are familiar from within, not from a case study.',
  },
  {
    n: '02',
    title: 'Formally grounded',
    body: 'Dual certification from the Family Firm Institute and an Honours degree in Industrial Psychology provide the structure behind the work.',
  },
  {
    n: '03',
    title: 'Family-first',
    body: 'The family is the client — not an individual, and not the balance sheet.',
  },
];

const CREDENTIALS = [
  'Certificate in Family Wealth Advisory — Family Firm Institute (USA)',
  'Certificate in Family Business Advisory — Family Firm Institute (USA)',
  'Honours, Industrial Psychology — University of the Witwatersrand',
  'Seventeen years within a family business',
];

const AboutPage = () => {
  return (
    <div className="bg-cream">
      <PageHero
        eyebrow="About"
        title="Someone who has been where you are"
        subtitle="These challenges were lived first-hand, then studied formally — so other families need not learn them the hard way."
      />

      {/* Story */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <figure className="text-center lg:text-left">
              <div className="inline-block rounded-full p-2 ring-1 ring-secondary-200">
                <img
                  src="/Amyphoto.png"
                  alt="Amy Jaffe — Founder of The Family Legacy Bridge"
                  className="block w-52 h-52 sm:w-60 sm:h-60 rounded-full object-cover grayscale-[0.35] contrast-[1.02]"
                />
              </div>
              <figcaption className="mt-7">
                <p className="font-serif text-2xl text-secondary-900">
                  Amy Jaffe
                </p>
                <p className="text-sm text-secondary-500 mt-1.5">
                  Founder, The Family Legacy Bridge
                </p>
              </figcaption>
            </figure>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-8">
              Why this work
            </h2>
            <div className="space-y-6 text-lg text-secondary-600 leading-relaxed">
              <p>
                Wealth rarely fractures a family because of the numbers. It
                fractures because the people behind them never aligned on what
                it was for — and never had a safe way to say so.
              </p>
              <p>
                Seventeen years inside a family business made the pattern
                impossible to ignore: capable, well-intentioned families
                drifting apart for want of an honest conversation no one knew
                how to begin.
              </p>
              <p>
                That led to formal training with the Family Firm Institute and
                an Honours degree in Industrial Psychology — practice paired
                with method.
              </p>
            </div>
            <blockquote className="mt-10 pt-10 border-t border-secondary-200 font-serif text-2xl text-secondary-900 leading-relaxed">
              “I have been through it myself. That is what drives the commitment
              to helping families get this right — before, not after.”
            </blockquote>
          </div>
        </div>
      </Section>

      {/* Approach */}
      <Section divider className="bg-white">
        <SectionHeading eyebrow="The approach" title="Lived first, studied since" />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {PILLARS.map((p) => (
            <div
              key={p.n}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-secondary-200"
            >
              <span className="md:col-span-2 font-serif text-2xl text-secondary-300">
                {p.n}
              </span>
              <h3 className="md:col-span-4 font-serif text-2xl font-normal text-secondary-900 leading-snug">
                {p.title}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed text-lg">
                {p.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Credentials */}
      <Section divider>
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em]">
              Credentials
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="border-t border-secondary-200">
              {CREDENTIALS.map((c) => (
                <p
                  key={c}
                  className="py-5 border-b border-secondary-200 text-secondary-700 text-lg"
                >
                  {c}
                </p>
              ))}
            </div>
            {/*
              PROOF PLACEHOLDER — client reflections / outcomes can be added
              here once available. Not rendered until real proof exists.
            */}
            <div className="mt-12">
              <BookCall size="lg">Request a conversation</BookCall>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default AboutPage;
